import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format";
import { AMOUNT_RANGES } from "@/lib/amount-ranges";
import {
  ActionBar,
  ActionButton,
  Eyebrow,
  ScreenLead,
  ScreenTitle,
} from "../OnboardingShell";

export type AmountRange = { min: number; max: number | null };

const RANGE_DETAIL = [
  "Obras chicas y medianas",
  "El paso siguiente en tamaño",
  "Requiere más experiencia acreditada",
  "Obras grandes, pocas al año",
];

/**
 * Pregunta 3 del contratista: el monto de las obras a las que postula. Cada
 * rango dice cuántas obras convocadas hay hoy en él.
 */
export function SizeStep({
  value,
  counts,
  allWorks,
  saving,
  onChange,
  onNext,
  onBack,
}: {
  value: AmountRange | null;
  /** Obras por rango, en el orden de `AMOUNT_RANGES`. `null` mientras cargan. */
  counts: number[] | null;
  /** Todas, sin rango (incluye las que no traen monto). */
  allWorks: number | null;
  saving: boolean;
  onChange: (range: AmountRange | null) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const busiest =
    counts && counts.some((count) => count > 0)
      ? counts.indexOf(Math.max(...counts))
      : -1;
  const options = [
    ...AMOUNT_RANGES.map((range, index) => ({
      key: range.label,
      range: { min: range.min, max: range.max } as AmountRange | null,
      label: range.label,
      detail: index === busiest ? "El rango con más obras en tu rubro" : RANGE_DETAIL[index],
      count: counts?.[index] ?? null,
    })),
    {
      key: "any",
      range: null,
      label: "Cualquier monto",
      detail: "Te mostramos todas",
      count: allWorks,
    },
  ];

  return (
    <div>
      <Eyebrow tone="blue">Pregunta 3 de 3</Eyebrow>
      <ScreenTitle>¿De qué tamaño?</ScreenTitle>
      <ScreenLead>El monto referencial de las obras a las que postulas.</ScreenLead>

      <div className="mt-6 flex max-w-[620px] flex-col gap-2.5">
        {options.map((option) => {
          const selected =
            option.range == null
              ? value == null
              : value?.min === option.range.min && value?.max === option.range.max;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.range)}
              aria-pressed={selected}
              className={cn(
                "flex cursor-pointer items-center gap-4 rounded-[16px] border-2 bg-white px-4 py-3.5 text-left transition-colors",
                selected ? "border-blue-700 bg-blue-50/60" : "border-slate-200 hover:border-slate-300",
              )}
            >
              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border-2",
                  selected ? "border-blue-700" : "border-slate-300",
                )}
              >
                {selected && <span className="size-2.5 rounded-full bg-blue-700" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[16px] font-bold text-slate-900">{option.label}</span>
                <span className="block text-[13px] text-slate-500">{option.detail}</span>
              </span>
              {option.count != null && (
                <span
                  className={cn(
                    "shrink-0 rounded-lg px-2.5 py-1 font-mono text-[12px] font-semibold",
                    selected ? "bg-blue-700 text-white" : "bg-blue-50 text-blue-800",
                  )}
                >
                  {formatNumber(option.count)} {option.count === 1 ? "obra" : "obras"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <ActionBar onBack={onBack}>
        <ActionButton tone="blue" onClick={onNext} loading={saving}>
          Ver mis obras
        </ActionButton>
      </ActionBar>
    </div>
  );
}
